/**
 * ABC Classification Dashboard
 * Tabbed interface for ABC analysis by category, ABC-XYZ matrix, category summary, and settings.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Slider,
  Snackbar,
  Divider,
} from '@mui/material';
import {
  Category as CategoryIcon,
  FileDownload as ExportIcon,
  GridOn as MatrixIcon,
  Summarize as SummaryIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import api from '../../services/api';
import { fetchFilterOptions, FilterOptionsData } from '../../services/filtersApi';
import TabPanel from '../Common/TabPanel';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import { CHART } from '../../constants/chartColors';

// ---------- Constants ----------

const ABC_COLORS: Record<string, string> = {
  A: '#e8f5e9',
  B: '#e3f2fd',
  C: '#fff3e0',
  D: '#fce4ec',
  E: '#f5f5f5',
};

const ABC_TEXT_COLORS: Record<string, string> = {
  A: '#2e7d32',
  B: '#1565c0',
  C: '#e65100',
  D: '#c62828',
  E: '#616161',
};

/* Solid colours for charts (bars, pie slices) — sourced from the canonical
 * IRIS chart palette so the ABC visuals match every other report in the tool. */
const ABC_CHART_COLORS: Record<string, string> = CHART.abc;

/* Standard tooltip styling shared with other report charts */
const chartTooltipStyle = {
  background: CHART.tooltipBg,
  border: `1px solid ${CHART.tooltipBorder}`,
  borderRadius: '8px',
  boxShadow: '0 4px 20px rgba(114,47,55,0.08)',
  fontSize: '12px',
  padding: '10px 14px',
};

const CATEGORIES = ['All', 'Parfum', 'Maquillage', 'Skincare', 'Beauty Tools', 'Travel'];

const DATA_SOURCE_OPTIONS = [
  { value: 'last_6m_sales', labelKey: 'abc.last6mSales' },
  { value: 'last_9m_sales', labelKey: 'abc.last9mSales' },
  { value: 'last_12m_sales', labelKey: 'abc.last12mSales' },
  { value: 'next_6m_forecast', labelKey: 'abc.next6mForecast' },
  { value: 'next_9m_forecast', labelKey: 'abc.next9mForecast' },
  { value: 'next_12m_forecast', labelKey: 'abc.next12mForecast' },
];

const DEFAULT_SETTINGS = {
  dataSource: 'last_6m_sales',
  scope: 'overall',
  thresholds: { A: 50, B: 70, C: 85, D: 95 },
};

/** Parse data_source string to derive period_months for API calls */
function parseDataSource(ds: string): { sourceType: string; periodMonths: number } {
  const map: Record<string, { sourceType: string; periodMonths: number }> = {
    last_6m_sales: { sourceType: 'sales', periodMonths: 6 },
    last_9m_sales: { sourceType: 'sales', periodMonths: 9 },
    last_12m_sales: { sourceType: 'sales', periodMonths: 12 },
    next_6m_forecast: { sourceType: 'forecast', periodMonths: 6 },
    next_9m_forecast: { sourceType: 'forecast', periodMonths: 9 },
    next_12m_forecast: { sourceType: 'forecast', periodMonths: 12 },
  };
  return map[ds] || { sourceType: 'sales', periodMonths: 6 };
}

// ---------- Component ----------

const ABCClassificationDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { fmtCurrency, fmtCurrencyPrecise } = useCurrencyFormat();
  const queryClient = useQueryClient();

  // Shared state
  const [tabValue, setTabValue] = useState(0);

  // Settings state
  const [dataSource, setDataSource] = useState(DEFAULT_SETTINGS.dataSource);
  const [classificationScope, setClassificationScope] = useState(DEFAULT_SETTINGS.scope);
  const [thresholds, setThresholds] = useState<{ A: number; B: number; C: number; D: number }>(DEFAULT_SETTINGS.thresholds);
  const [brandCategoryThresholds, setBrandCategoryThresholds] = useState<
    Record<string, Record<string, { A: number; B: number; C: number; D: number }>>
  >({});
  const [categoryThresholds, setCategoryThresholds] = useState<
    Record<string, { A: number; B: number; C: number; D: number }>
  >({});
  const [selectedSettingsBrand, setSelectedSettingsBrand] = useState<string>('');

  // Summary tab filters
  const [summaryCategory, setSummaryCategory] = useState<string>('');
  const [summaryBrand, setSummaryBrand] = useState<string>('');

  // Tab 2 state
  const [matrixCategory, setMatrixCategory] = useState<string>('All');
  const [matrixDialogOpen, setMatrixDialogOpen] = useState(false);
  const [matrixDialogCell, setMatrixDialogCell] = useState<{ abc: string; xyz: string; products: any[] } | null>(null);

  // Category filter for settings
  const [selectedSettingsCategory, setSelectedSettingsCategory] = useState<string>('All');

  // Preview state
  const [previewData, setPreviewData] = useState<{
    A: number; B: number; C: number; D: number; E: number; total: number;
    categories: Record<string, { A: number; B: number; C: number; D: number; E: number; total: number; total_revenue: number }>;
  } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Derived period from data source
  const { periodMonths } = parseDataSource(dataSource);

  // Common query params for all ABC endpoints
  const commonParams = {
    data_source: dataSource,
    scope: 'overall',
    threshold_a: thresholds.A,
    threshold_b: thresholds.B,
    threshold_c: thresholds.C,
    threshold_d: thresholds.D,
  };

  // ---- Filter options ----
  const { data: filterOptions } = useQuery<FilterOptionsData>({
    queryKey: ['filterOptions'],
    queryFn: () => fetchFilterOptions(),
    staleTime: 5 * 60 * 1000,
  });

  // ---- Load saved settings on mount ----
  const { data: savedSettings } = useQuery({
    queryKey: ['abcSettings'],
    queryFn: async () => {
      const response = await api.getAbcSettings();
      return response.data;
    },
    staleTime: Infinity,
  });

  useEffect(() => {
    if (savedSettings) {
      if (savedSettings.data_source) setDataSource(savedSettings.data_source);
      if (savedSettings.scope) setClassificationScope(savedSettings.scope);
      if (savedSettings.thresholds) {
        setThresholds({
          A: savedSettings.thresholds.A ?? 50,
          B: savedSettings.thresholds.B ?? 70,
          C: savedSettings.thresholds.C ?? 85,
          D: savedSettings.thresholds.D ?? 95,
        });
      }
      if (savedSettings.brand_category_thresholds) {
        setBrandCategoryThresholds(savedSettings.brand_category_thresholds);
      }
      if (savedSettings.category_thresholds) {
        setCategoryThresholds(savedSettings.category_thresholds);
      }
    }
  }, [savedSettings]);

  // ---- Queries ----

  const {
    data: matrixData,
    isLoading: matrixLoading,
    error: matrixError,
  } = useQuery({
    queryKey: ['abcXyzMatrix', matrixCategory, dataSource, thresholds.A, thresholds.B, thresholds.C, thresholds.D],
    queryFn: async () => {
      const params: any = {
        period_months: periodMonths,
        ...commonParams,
      };
      if (matrixCategory !== 'All') params.category = matrixCategory;
      const response = await api.getAbcXyzMatrix(params);
      return response.data;
    },
  });

  const {
    data: summaryData,
    isLoading: summaryLoading,
    error: summaryError,
  } = useQuery({
    queryKey: ['abcCategorySummary', dataSource, thresholds.A, thresholds.B, thresholds.C, thresholds.D, summaryCategory, summaryBrand],
    queryFn: async () => {
      const params: any = {
        period_months: periodMonths,
        ...commonParams,
      };
      if (summaryBrand) params.brand = summaryBrand;
      const response = await api.getAbcCategorySummary(params);
      return response.data;
    },
  });

  // ---- Debounced preview ----
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const response = await api.previewAbcClassification({
        thresholds,
        data_source: dataSource,
        scope: 'overall',
        period_months: periodMonths,
        ...(selectedSettingsBrand ? { brand: selectedSettingsBrand } : {}),
        ...(Object.keys(brandCategoryThresholds).length > 0 ? { brand_category_thresholds: brandCategoryThresholds } : {}),
        ...(Object.keys(categoryThresholds).length > 0 ? { category_thresholds: categoryThresholds } : {}),
      });
      setPreviewData(response.data);
    } catch {
      setPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [thresholds, dataSource, periodMonths, selectedSettingsBrand, brandCategoryThresholds, categoryThresholds]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchPreview, 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [fetchPreview]);

  // ---- Handlers ----

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleExportExcel = () => {
    alert(t('abc.exportPlaceholder'));
  };

  const handleSaveSettings = async () => {
    try {
      await api.saveAbcSettings({
        data_source: dataSource,
        scope: 'overall',
        thresholds,
        brand_category_thresholds: brandCategoryThresholds,
        category_thresholds: categoryThresholds,
      });
      queryClient.invalidateQueries({ queryKey: ['abcSettings'] });
      queryClient.invalidateQueries({ queryKey: ['abcByCategory'] });
      queryClient.invalidateQueries({ queryKey: ['abcXyzMatrix'] });
      queryClient.invalidateQueries({ queryKey: ['abcCategorySummary'] });
      setSnackbar({ open: true, message: t('abc.settingsSaved'), severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: t('abc.settingsSaveError'), severity: 'error' });
    }
  };

  const handleResetSettings = () => {
    setDataSource(DEFAULT_SETTINGS.dataSource);
    setClassificationScope(DEFAULT_SETTINGS.scope);
    setThresholds({ ...DEFAULT_SETTINGS.thresholds });
    setBrandCategoryThresholds({});
    setCategoryThresholds({});
    setSelectedSettingsBrand('');
    setSelectedSettingsCategory('All');
  };

  // ---- Derived data for Tab 2 (Matrix) ----

  const xyzLabels = ['X', 'Y', 'Z'];
  const xyzDescriptions: Record<string, string> = { X: 'Stable', Y: 'Variable', Z: 'Erratic' };
  const abcLabels = ['A', 'B', 'C', 'D', 'E'];

  const getMatrixCell = (abc: string, xyz: string) => {
    if (!matrixData?.matrix) return { count: 0, total_revenue: 0, products: [] };
    const cellKey = `${abc}${xyz}`;
    const cell = matrixData.matrix[cellKey];
    return cell || { count: 0, total_revenue: 0, products: [] };
  };

  const matrixMaxRevenue = React.useMemo(() => {
    if (!matrixData?.matrix || typeof matrixData.matrix !== 'object') return 1;
    const values = Object.values(matrixData.matrix) as any[];
    return Math.max(...values.map((c: any) => c.total_revenue || 0), 1);
  }, [matrixData]);

  // ---- Derived data for Tab 3 (Summary) ----

  const filteredSummaryCategories = React.useMemo(() => {
    if (!summaryData || !Array.isArray(summaryData)) return [];
    if (!summaryCategory) return summaryData;
    return summaryData.filter((c: any) => c.category === summaryCategory);
  }, [summaryData, summaryCategory]);

  const summaryChartData = React.useMemo(() => {
    return filteredSummaryCategories.map((c: any) => ({
      name: c.category,
      A: c.a_count || 0,
      B: c.b_count || 0,
      C: c.c_count || 0,
      D: c.d_count || 0,
      E: c.e_count || 0,
    }));
  }, [filteredSummaryCategories]);

  // ---- Render ----

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CategoryIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4">{t('abc.title')}</Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Export Button */}
          <Button variant="outlined" startIcon={<ExportIcon />} onClick={handleExportExcel}>
            {t('abc.exportExcel')}
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab icon={<SettingsIcon />} iconPosition="start" label={t('abc.calculation')} />
        <Tab icon={<MatrixIcon />} iconPosition="start" label={t('abc.matrix')} />
        <Tab icon={<SummaryIcon />} iconPosition="start" label={t('abc.categorySummary')} />
      </Tabs>

      {/* ======================== ABC-XYZ Matrix ======================== */}
      <TabPanel value={tabValue} index={1}>
        {matrixLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {matrixError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('abc.errorLoading')}
          </Alert>
        )}
        {!matrixLoading && !matrixError && (
          <>
            {/* Category filter */}
            <Box sx={{ mb: 3 }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>{t('abc.category')}</InputLabel>
                <Select
                  value={matrixCategory}
                  label={t('abc.category')}
                  onChange={(e) => setMatrixCategory(e.target.value)}
                >
                  {CATEGORIES.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Matrix Grid */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('abc.matrixTitle')}
              </Typography>

              {/* Column headers */}
              <Grid container spacing={1} sx={{ mb: 1 }}>
                <Grid item xs={1.5} />
                {abcLabels.map((abc) => (
                  <Grid item xs={(10.5 / 5)} key={abc}>
                    <Typography variant="subtitle2" align="center" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                      {t('abc.classLabel', { class: abc })}
                    </Typography>
                  </Grid>
                ))}
              </Grid>

              {/* Rows */}
              {xyzLabels.map((xyz) => (
                <Grid container spacing={1} key={xyz} sx={{ mb: 1 }}>
                  {/* Row header */}
                  <Grid
                    item
                    xs={1.5}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                      {xyz} ({xyzDescriptions[xyz]})
                    </Typography>
                  </Grid>

                  {/* Cells */}
                  {abcLabels.map((abc) => {
                    const cell = getMatrixCell(abc, xyz);
                    const intensity = matrixMaxRevenue > 0 ? (cell.total_revenue || 0) / matrixMaxRevenue : 0;
                    const bgColor = ABC_COLORS[abc];
                    const opacity = 0.1 + intensity * 0.6;

                    return (
                      <Grid item xs={(10.5 / 5)} key={`${abc}-${xyz}`}>
                        <Paper
                          elevation={2}
                          sx={{
                            p: 2,
                            textAlign: 'center',
                            cursor: 'pointer',
                            backgroundColor: bgColor,
                            opacity,
                            transition: 'transform 0.15s, box-shadow 0.15s',
                            '&:hover': {
                              transform: 'scale(1.03)',
                              boxShadow: 'var(--shadow-md)',
                              opacity: Math.min(opacity + 0.15, 1),
                            },
                          }}
                          onClick={() => {
                            setMatrixDialogCell({
                              abc,
                              xyz,
                              products: cell.products || [],
                            });
                            setMatrixDialogOpen(true);
                          }}
                        >
                          <Typography variant="h5" fontWeight="bold" color="#fff">
                            {cell.count || 0}
                          </Typography>
                          <Typography variant="caption" color="#fff">
                            {t('abc.products')}
                          </Typography>
                          <Typography variant="body2" color="#fff" sx={{ mt: 0.5 }}>
                            {fmtCurrency(cell.total_revenue || 0)}
                          </Typography>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              ))}
            </Paper>

            {/* Cell Detail Dialog */}
            <Dialog
              open={matrixDialogOpen}
              onClose={() => setMatrixDialogOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>
                {matrixDialogCell
                  ? `${t('abc.classLabel', { class: matrixDialogCell.abc })} / ${matrixDialogCell.xyz} (${xyzDescriptions[matrixDialogCell.xyz]})`
                  : ''}
              </DialogTitle>
              <DialogContent dividers>
                {matrixDialogCell && matrixDialogCell.products.length > 0 ? (
                  <List dense>
                    {matrixDialogCell.products.map((p: any, idx: number) => (
                      <ListItem key={p.sku || idx}>
                        <ListItemText
                          primary={p.name || p.product_name || p.sku}
                          secondary={`SKU: ${p.sku || '-'} | ${t('abc.revenue')}: ${fmtCurrency(p.revenue || 0)}`}
                        />
                        <Chip
                          label={`${matrixDialogCell.abc}${matrixDialogCell.xyz}`}
                          size="small"
                          variant="filled"
                          sx={{
                            backgroundColor: ABC_COLORS[matrixDialogCell.abc],
                            color: ABC_TEXT_COLORS[matrixDialogCell.abc] || '#616161',
                            fontWeight: 700,
                            fontSize: '11px',
                            height: 20,
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    {t('abc.noData')}
                  </Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setMatrixDialogOpen(false)}>{t('abc.close')}</Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </TabPanel>

      {/* ======================== Category Summary ======================== */}
      <TabPanel value={tabValue} index={2}>
        {summaryLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {summaryError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('abc.errorLoading')}
          </Alert>
        )}
        {!summaryLoading && !summaryError && (
          <>
            {/* Category / Brand Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>{t('abc.category')}</InputLabel>
                <Select
                  value={summaryCategory}
                  label={t('abc.category')}
                  onChange={(e) => setSummaryCategory(e.target.value)}
                >
                  <MenuItem value="">{t('abc.allCategories')}</MenuItem>
                  {(filterOptions?.categories || []).map((c: string) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>{t('abc.brand')}</InputLabel>
                <Select
                  value={summaryBrand}
                  label={t('abc.brand')}
                  onChange={(e) => setSummaryBrand(e.target.value)}
                >
                  <MenuItem value="">{t('abc.allBrands')}</MenuItem>
                  {(filterOptions?.brands || []).map((b: string) => (
                    <MenuItem key={b} value={b}>
                      {b}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Stacked Bar Chart */}
            <Paper
              sx={{
                p: 3,
                mb: 3,
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              <Typography
                sx={{
                  fontSize: 'var(--text-base)',
                  fontWeight: 600,
                  color: 'var(--color-text-primary)',
                  fontFamily: 'var(--font-heading)',
                  mb: 2,
                }}
              >
                {t('abc.distributionByCategory')}
              </Typography>
              <Box sx={{ height: 360 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={summaryChartData}
                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11, fill: CHART.tick }}
                      tickLine={false}
                      axisLine={{ stroke: CHART.axis }}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: CHART.tick }}
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip contentStyle={chartTooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    <Bar dataKey="A" stackId="abc" fill={ABC_CHART_COLORS.A} name={t('abc.classA')} />
                    <Bar dataKey="B" stackId="abc" fill={ABC_CHART_COLORS.B} name={t('abc.classB')} />
                    <Bar dataKey="C" stackId="abc" fill={ABC_CHART_COLORS.C} name={t('abc.classC')} />
                    <Bar dataKey="D" stackId="abc" fill={ABC_CHART_COLORS.D} name={t('abc.classD')} />
                    <Bar
                      dataKey="E"
                      stackId="abc"
                      fill={ABC_CHART_COLORS.E}
                      name={t('abc.classE')}
                      radius={[3, 3, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>

            {/* Summary Table */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t('abc.summaryTable')}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('abc.category')}</TableCell>
                      <TableCell align="right">{t('abc.totalProducts')}</TableCell>
                      <TableCell align="right">A</TableCell>
                      <TableCell align="right">B</TableCell>
                      <TableCell align="right">C</TableCell>
                      <TableCell align="right">D</TableCell>
                      <TableCell align="right">E</TableCell>
                      <TableCell align="right">{t('abc.totalRevenue')}</TableCell>
                      <TableCell align="right">% {t('abc.totalRevenue')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSummaryCategories.map((cat: any, idx: number) => (
                      <TableRow key={cat.category || idx} hover>
                        <TableCell>{cat.category}</TableCell>
                        <TableCell align="right">{cat.total_products || 0}</TableCell>
                        {['A', 'B', 'C', 'D', 'E'].map((cls) => (
                          <TableCell align="right" key={cls}>
                            <Chip
                              label={`${cat[`${cls.toLowerCase()}_count`] || 0} (${cat[`${cls.toLowerCase()}_pct`] || 0}%)`}
                              size="small"
                              sx={{ backgroundColor: ABC_COLORS[cls], color: ABC_TEXT_COLORS[cls] || '#616161', fontWeight: 700, fontSize: '11px', height: 20 }}
                            />
                          </TableCell>
                        ))}
                        <TableCell align="right">
                          {fmtCurrency(cat.total_revenue || 0)}
                        </TableCell>
                        <TableCell align="right">{cat.revenue_pct_of_total || 0}%</TableCell>
                      </TableRow>
                    ))}
                    {filteredSummaryCategories.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            {t('abc.noData')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}
      </TabPanel>

      {/* ======================== Calculation ======================== */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {/* Filters Row: Brand | Calculation Rule | Category */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                {t('abc.brand')}
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>{t('abc.brand')}</InputLabel>
                <Select
                  value={selectedSettingsBrand}
                  label={t('abc.brand')}
                  onChange={(e) => setSelectedSettingsBrand(e.target.value)}
                >
                  <MenuItem value="">{t('abc.allBrandsOption')}</MenuItem>
                  {(filterOptions?.brands || []).map((b: string) => (
                    <MenuItem key={b} value={b}>{b}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                {t('abc.calculationRule')}
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>{t('abc.dataSource')}</InputLabel>
                <Select
                  value={dataSource}
                  label={t('abc.dataSource')}
                  onChange={(e) => setDataSource(e.target.value)}
                >
                  {DATA_SOURCE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                {t('abc.category')}
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>{t('abc.category')}</InputLabel>
                <Select
                  value={selectedSettingsCategory}
                  label={t('abc.category')}
                  onChange={(e) => setSelectedSettingsCategory(e.target.value)}
                >
                  <MenuItem value="All">{t('abc.allCategories')}</MenuItem>
                  {(filterOptions?.categories || []).map((c: string) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Paper>
          </Grid>

          {/* Per-category cards: each with its own thresholds + classification results */}
          {previewLoading ? (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            </Grid>
          ) : previewData?.categories ? (
            Object.entries(previewData.categories)
              .filter(([catName]) => selectedSettingsCategory === 'All' || catName === selectedSettingsCategory)
              .sort(([, a], [, b]) => b.total_revenue - a.total_revenue)
              .map(([catName, catData]) => {
                // Resolve effective thresholds: brand_category > category > global
                const brandOverride = selectedSettingsBrand
                  ? brandCategoryThresholds[selectedSettingsBrand]?.[catName]
                  : undefined;
                const catOverride = categoryThresholds[catName];
                const override = brandOverride || catOverride;
                const effectiveA = override?.A ?? thresholds.A;
                const effectiveB = override?.B ?? thresholds.B;
                const effectiveC = override?.C ?? thresholds.C;
                const effectiveD = override?.D ?? thresholds.D;

                const hasDEClasses = effectiveC < 100;

                const updateThreshold = (key: 'A' | 'B' | 'C' | 'D', value: number) => {
                  let newA = key === 'A' ? value : effectiveA;
                  let newB = key === 'B' ? value : effectiveB;
                  let newC = key === 'C' ? value : effectiveC;
                  let newD = key === 'D' ? value : effectiveD;
                  // When C reaches 100, push D to 100 too
                  if (newC >= 100) { newC = 100; newD = 100; }
                  // Ensure D >= C when C < 100
                  if (newC < 100 && newD <= newC) { newD = Math.min(newC + 1, 100); }
                  if (selectedSettingsBrand) {
                    setBrandCategoryThresholds((prev) => ({
                      ...prev,
                      [selectedSettingsBrand]: {
                        ...prev[selectedSettingsBrand],
                        [catName]: { A: newA, B: newB, C: newC, D: newD },
                      },
                    }));
                  } else {
                    // No brand selected — apply to ALL brands uniformly.
                    // Set the category-level threshold...
                    const newCatThreshold = { A: newA, B: newB, C: newC, D: newD };
                    setCategoryThresholds((prev) => ({
                      ...prev,
                      [catName]: newCatThreshold,
                    }));
                    // ...and clear any brand-specific overrides for this category
                    // so no brand keeps a stale per-brand threshold that would
                    // take priority over the category-wide setting.
                    setBrandCategoryThresholds((prev) => {
                      const updated = { ...prev };
                      let changed = false;
                      for (const brand of Object.keys(updated)) {
                        if (updated[brand]?.[catName]) {
                          const { [catName]: _, ...rest } = updated[brand];
                          updated[brand] = rest;
                          changed = true;
                          if (Object.keys(rest).length === 0) {
                            delete updated[brand];
                          }
                        }
                      }
                      return changed ? updated : prev;
                    });
                  }
                };

                const activeClasses = hasDEClasses
                  ? ['A', 'B', 'C', 'D', 'E'] as const
                  : ['A', 'B', 'C'] as const;

                return (
                  <Grid item xs={12} md={selectedSettingsCategory === 'All' ? 4 : 6} key={catName}>
                    <Paper sx={{ p: 3, height: '100%' }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {catName}
                      </Typography>

                      {/* Threshold sliders */}
                      {([
                        { key: 'A' as const, value: effectiveA, color: ABC_CHART_COLORS.A, min: 5, max: effectiveB - 1 },
                        { key: 'B' as const, value: effectiveB, color: ABC_CHART_COLORS.B, min: effectiveA + 1, max: effectiveC - 1 },
                        { key: 'C' as const, value: effectiveC, color: ABC_CHART_COLORS.C, min: effectiveB + 1, max: 100 },
                        ...(hasDEClasses ? [{ key: 'D' as const, value: effectiveD, color: ABC_CHART_COLORS.D, min: effectiveC + 1, max: 99 }] : []),
                      ]).map(({ key, value, color, min, max }) => (
                        <Box key={key} sx={{ mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {t(`abc.threshold${key}`)}: {value}%
                          </Typography>
                          <Slider
                            value={value}
                            onChange={(_e, val) => {
                              const clamped = Math.max(min, Math.min(max, val as number));
                              updateThreshold(key, clamped);
                            }}
                            min={min}
                            max={max}
                            step={1}
                            size="small"
                            valueLabelDisplay="auto"
                            sx={{ color, py: 0.5 }}
                          />
                        </Box>
                      ))}

                      <Typography variant="caption" color="text.secondary">
                        {hasDEClasses
                          ? `A: 0–${effectiveA}% | B: ${effectiveA}–${effectiveB}% | C: ${effectiveB}–${effectiveC}% | D: ${effectiveC}–${effectiveD}% | E: ${effectiveD}–100%`
                          : `A: 0–${effectiveA}% | B: ${effectiveA}–${effectiveB}% | C: ${effectiveB}–100%`
                        }
                      </Typography>

                      <Divider sx={{ my: 1.5 }} />

                      {/* Classification results */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                        {activeClasses.map((cls) => (
                          <Chip
                            key={cls}
                            label={`${cls}: ${(catData as any)[cls]}`}
                            size="small"
                            sx={{ backgroundColor: ABC_COLORS[cls], color: ABC_TEXT_COLORS[cls] || '#616161', fontWeight: 700, fontSize: '11px', height: 20, minWidth: 55 }}
                          />
                        ))}
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t('abc.totalProducts')}: {catData.total} SKUs
                      </Typography>

                      <Typography variant="body2" color="text.secondary">
                        {t('abc.turnover')}
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        {fmtCurrencyPrecise(catData.total_revenue)}
                      </Typography>
                    </Paper>
                  </Grid>
                );
              })
          ) : (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {t('abc.noData')}
              </Typography>
            </Grid>
          )}

          {/* Save / Reset Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={handleResetSettings}>
                {t('abc.reset')}
              </Button>
              <Button variant="contained" onClick={handleSaveSettings}>
                {t('abc.save')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ABCClassificationDashboard;
